import axios, {
  AxiosInstance,
  InternalAxiosRequestConfig,
  AxiosResponse,
  AxiosRequestHeaders,
} from 'axios';
import { getToken, isTokenExpired, removeToken, getRefreshToken, removeRefreshToken, setToken, setRefreshToken } from '../utils/auth';
import { refreshTokenApi } from './authService';

// Point to the new base URL
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';

const apiService: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
apiService.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();

    if (token) {
      if (isTokenExpired(token)) {
        removeToken();
        window.location.href = '/login';
        return Promise.reject('Token expired');
      }
      if (config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        config.headers = { Authorization: `Bearer ${token}` } as AxiosRequestHeaders;
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Token refresh handling
let isRefreshing = false;
// Store pending requests that should be retried after token refresh
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// Response interceptor
apiService.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const originalRequest = error.config;

    // If error is 401 and we haven't tried to refresh the token yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If we're already refreshing, add this request to the queue
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return apiService(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      // Try to refresh the token
      try {
        const refreshToken = getRefreshToken();

        if (!refreshToken) {
          // No refresh token available, logout
          removeToken();
          removeRefreshToken();
          window.location.href = '/login';
          return Promise.reject(error);
        }

        // Call the refresh token API with the refresh token
        const response = await refreshTokenApi(refreshToken);
        const { token, refreshToken: newRefreshToken } = response;

        // Update the token in localStorage
        setToken(token);

        // If a new refresh token is returned, update it as well
        if (newRefreshToken) {
          setRefreshToken(newRefreshToken);
        }

        // Update Authorization header
        originalRequest.headers['Authorization'] = `Bearer ${token}`;

        // Process the queue with the new token
        processQueue(null, token);

        // Retry the original request
        return apiService(originalRequest);
      } catch (refreshError) {
        // If refresh token fails, logout
        processQueue(refreshError, null);
        removeToken();
        removeRefreshToken();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For other errors
    if (error.response) {
      const errorMessage = error.response.data?.message || 'An error occurred';
      return Promise.reject({ ...error, message: errorMessage });
    }

    return Promise.reject(error);
  }
);

export default apiService;
